/**
 * Shared helpers for cycle tasklist bulk add / bulk update upload flows.
 * Used by TasksLandingPage and BulkUpload.
 */

/** Summary key from validate APIs — shown under the table, not on a field cell. */
export const BULK_UPLOAD_ROW_SUMMARY_KEY = '_row';

/**
 * Column aliases: API may emit plural/alternate keys for the same preview column.
 * Treated as mapped to the column so they stay on the row (not the footer).
 */
const FIELD_ERROR_COLUMN_ALIASES = Object.freeze({
  sign_off_approvers: 'sign_off_approver',
});

/**
 * Normalize validate API envelope into a consistent result.
 * @param {object} validation - Store validate action return value
 * @returns {{ valid: boolean, rowErrors: Array, message: string, result: object }}
 */
export function parseCycleTaskValidateResult(validation) {
  const result = validation?.data || {};
  const rowErrors = Array.isArray(result.rowErrors) ? result.rowErrors : [];
  const valid = result.valid === true
    || (validation?.status === 'success' && !rowErrors.length);
  return {
    valid,
    rowErrors,
    message: validation?.message
      || (valid ? '' : 'Validation failed'),
    result,
  };
}

/**
 * Merge validate rowErrors ({ row: 1-based, fields }) onto preview grid error maps.
 * @param {Array} existingErrors
 * @param {Array<{ row: number, fields: Record<string, string> }>} rowErrors
 * @param {'object'|'arrayOrObject'} shape - bulkEdit uses object; checklist may store array rows
 * @returns {{ errors: Array, hasErrors: boolean }}
 */
export function mergeCycleTaskRowErrors(existingErrors = [], rowErrors = [], shape = 'object') {
  if (!Array.isArray(rowErrors) || !rowErrors.length) {
    return { errors: existingErrors, hasErrors: false };
  }
  const next = Array.isArray(existingErrors)
    ? existingErrors.map((row) => {
      if (shape === 'arrayOrObject' && Array.isArray(row)) return [...row];
      return { ...(row || {}) };
    })
    : [];
  rowErrors.forEach((entry) => {
    const idx = Number(entry.row) - 1;
    if (!Number.isFinite(idx) || idx < 0) return;
    while (next.length <= idx) next.push({});
    const fields = entry.fields || {};
    if (shape === 'arrayOrObject' && Array.isArray(next[idx])) {
      next[idx] = [{ ...fields }];
    } else {
      next[idx] = { ...(next[idx] || {}), ...fields };
    }
  });
  return { errors: next, hasErrors: true };
}

function stringifyFieldErrorMessage(message) {
  if (message == null || message === '') return '';
  if (Array.isArray(message)) {
    return message.map((m) => String(m).trim()).filter(Boolean).join('; ');
  }
  return String(message).trim();
}

/**
 * Resolve a preview-column field error from a row's `fields` map.
 * Accepts API aliases (e.g. sign_off_approvers → sign_off_approver).
 * @param {Record<string, string|string[]>} fields
 * @param {string} fieldKey
 * @returns {string}
 */
export function getRowFieldErrorMessage(fields = {}, fieldKey) {
  if (!fieldKey || !fields || typeof fields !== 'object') return '';
  const direct = stringifyFieldErrorMessage(fields[fieldKey]);
  if (direct) return direct;
  const aliasEntry = Object.entries(FIELD_ERROR_COLUMN_ALIASES)
    .find(([, columnKey]) => columnKey === fieldKey);
  if (aliasEntry) {
    return stringifyFieldErrorMessage(fields[aliasEntry[0]]);
  }
  return '';
}

/**
 * Field keys that count as "mapped" to a preview column (column value + aliases).
 * @param {Iterable<string>} columnKeys
 * @returns {Set<string>}
 */
function mappedFieldErrorKeys(columnKeys = []) {
  const mapped = new Set(columnKeys);
  Object.entries(FIELD_ERROR_COLUMN_ALIASES).forEach(([alias, columnKey]) => {
    if (mapped.has(columnKey)) mapped.add(alias);
  });
  return mapped;
}

/**
 * Footer list: only `_row` summary messages (never field-key messages).
 * @param {Record<string, string|string[]>} fields
 * @returns {string[]}
 */
export function pickNonRowFieldErrorMessages(fields = {}) {
  const msg = stringifyFieldErrorMessage(fields?.[BULK_UPLOAD_ROW_SUMMARY_KEY]);
  return msg ? [msg] : [];
}

/**
 * Field-level messages for the preview row (every key except `_row`).
 * @param {Record<string, string|string[]>} fields
 * @returns {string[]}
 */
export function pickRowFieldErrorMessages(fields = {}) {
  const out = [];
  Object.entries(fields || {}).forEach(([key, message]) => {
    if (key === BULK_UPLOAD_ROW_SUMMARY_KEY) return;
    const msg = stringifyFieldErrorMessage(message);
    if (msg) out.push(msg);
  });
  return out;
}

/**
 * Field errors whose keys are not preview columns (still shown on the row, not footer).
 * @param {Record<string, string|string[]>} fields
 * @param {Iterable<string>} columnKeys - preview header `value`s
 * @returns {string[]}
 */
export function pickUnmappedRowFieldErrorMessages(fields = {}, columnKeys = []) {
  const mapped = mappedFieldErrorKeys(columnKeys);
  const out = [];
  Object.entries(fields || {}).forEach(([key, message]) => {
    if (key === BULK_UPLOAD_ROW_SUMMARY_KEY) return;
    if (mapped.has(key)) return;
    const msg = stringifyFieldErrorMessage(message);
    if (msg) out.push(msg);
  });
  return out;
}

/**
 * Human-readable summary lines for bulk-edit footer (Row N: …).
 * Only includes `_row` messages so field errors are not duplicated under the table.
 * @param {Array<{ row: number, fields: Record<string, string> }>} rowErrors
 * @returns {string[]}
 */
export function formatBulkUpdateRowErrorMessages(rowErrors = []) {
  return (rowErrors || [])
    .map((entry) => {
      const msgs = pickNonRowFieldErrorMessages(entry?.fields || {});
      if (!msgs.length) return '';
      return `Row ${entry.row}: ${msgs.join('; ')}`;
    })
    .filter(Boolean);
}
